import { State } from 'src/replicant/State';
import {
  getJSTDate,
  getJSTDayOfTheWeek,
  sortedDaysOfTheWeek,
} from 'src/replicant/utils/time';
import {
  BotType,
  RaceConfig,
  RaceGoal,
  RaceType,
  TargetRaceConfig,
  TimedRaceConfig,
  botCompositions,
  botCompositionsV2,
  botCount,
  raceSchedule,
  raceUnlockLevel,
  racesConfig,
  rewardsConfig,
  scoreFactors,
} from './races.ruleset';
import { duration } from 'src/replicant/utils/duration';
import { Immutable, createPrng } from '@play-co/replicant';
import {
  generateRandomName,
  getRandomFloat,
  getRandomInt,
  getRandomItemFromArray,
} from 'src/replicant/utils/random';
import { RaceState } from './races.schema';
import { Locale } from 'src/replicant/ruleset/locale';
import { assertNever, mod } from 'src/replicant/utils';
import { getMatch2Level } from '../match2.getters';
import { BOT_AVATARS } from 'src/replicant/bots/assets';

/**
 * Get active race.
 * @param now Current time in milliseconds.
 * @returns
 */
export function getActiveRace(
  now: number,
): { id: string; config: RaceConfig } | null {
  const day = getJSTDayOfTheWeek(now);

  // No active race, bail out.
  if (raceSchedule[day] === null) {
    return null;
  }

  // Check if the event is running for more than one day.
  let currentDay = day;
  const previousDay = () => {
    const previousDayIndex = mod(
      sortedDaysOfTheWeek.indexOf(currentDay) - 1,
      sortedDaysOfTheWeek.length,
    );
    return sortedDaysOfTheWeek[previousDayIndex];
  };
  while (raceSchedule[day] === raceSchedule[previousDay()]) {
    currentDay = previousDay();
    now -= duration({ days: 1 });
  }
  const eventId = raceSchedule[currentDay];
  if (!eventId) {
    return null;
  }

  return {
    id: getJSTDate(now).toLocaleDateString('ja-JA'),
    config: racesConfig[eventId],
  };
}

/**
 * Get active race index.
 * @param state
 * @param now
 */
export function getActiveRaceIndex(state: State, now: number): number {
  const activeRace = getActiveRace(now);
  if (!activeRace) {
    return -1;
  }

  let index = (state.match2.races[activeRace.id]?.length ?? 1) - 1;

  while (state.match2.races[activeRace.id]?.[index]?.claimedRewardAt) {
    index++;
  }

  return index;
}

/**
 * Check if races are available.
 * @param state
 * @returns
 */
export function areRacesAvailable(state: State): boolean {
  // Must be unlocked.
  if (getMatch2Level(state) < raceUnlockLevel) {
    return false;
  }

  return true;
}

/**
 * Check if user can participate on the current race.
 * @param state
 * @param now
 * @returns
 */
export function canParticipateOnCurrentRace(
  state: State,
  now: number,
): boolean {
  // Races are not available.
  if (!areRacesAvailable(state)) {
    return false;
  }

  // No active race, can't participate.
  const activeRace = getActiveRace(now);
  if (!activeRace) {
    return false;
  }

  // Turtle races are not available for participation.
  // Per `races.ruleset.ts`, this theme is scheduled Fri–Sun.
  if (activeRace.config.theme === 'turtle') {
    return false;
  }

  // Check criteria.
  switch (activeRace.config.type) {
    case 'time':
      return canParticipateOnTimedRace(state, now, {
        id: activeRace.id,
        config: activeRace.config,
      });
    default:
      assertNever(activeRace.config);
  }
}

function canParticipateOnTimedRace(
  state: State,
  now: number,
  activeRace: { id: string; config: TimedRaceConfig },
) {
  const {
    id,
    config: { raceDurationHours },
  } = activeRace;

  if (!state.match2.races[id]) {
    return true;
  }

  // Check if user has already claimed rewards. Only for time travelers.
  if (state.match2.races[id][0].claimedRewardAt) {
    return false;
  }

  // Check if user has enough time to participate.
  // Timed events have always 1 race only.
  const { createdAt } = state.match2.races[id][0];
  const expiresAt = createdAt + duration({ hours: raceDurationHours });
  return now < expiresAt;
}

/**
 * Will return time until the end of the event, or how much time the player has left to play.
 * @param state
 * @param now
 */
export function getRemainingTimeForCurrentRace(
  state: State,
  now: number,
): number {
  const activeRace = getActiveRace(now);
  if (!activeRace) {
    return 0;
  }

  const raceState = getActiveRaceState(state, now);
  // User hasn't yet played event.
  if (!raceState) {
    const until = getRaceEndDate(activeRace.id);
    return until - now;
  }

  switch (activeRace.config.type) {
    case 'time':
      return getRemainingTimeForTimedRace(
        state,
        now,
        activeRace.id,
        activeRace.config,
      );
    case 'target':
      return getRemainingTimeForTargetRace(now, activeRace.id);
    default:
      assertNever(activeRace.config);
  }
}

function getRemainingTimeForTimedRace(
  state: State,
  now: number,
  id: string,
  config: TimedRaceConfig,
) {
  const raceState = getActiveRaceState(state, now);
  // User hasn't yet played event.
  if (!raceState) {
    const until = getRaceEndDate(id);
    return until - now;
  }

  let expiresAt =
    raceState.createdAt + duration({ hours: config.raceDurationHours });
  expiresAt = Math.min(expiresAt, getRaceEndDate(id));
  return Math.max(expiresAt - now, 0);
}

function getRemainingTimeForTargetRace(now: number, id: string) {
  const until = getRaceEndDate(id);
  return until - now;
}

/**
 * Get end date for a given race.
 * @param raceId
 */
export function getRaceEndDate(raceId: string): number {
  const raceStart = getRaceStartTime(raceId);
  let until = raceStart;
  while (getActiveRace(until)?.id === raceId) {
    until += duration({ days: 1 });
  }

  return until;
}

/**
 * Convert race id to timestamp.
 * @param raceId
 */
export function getRaceStartTime(raceId: string): number {
  return new Date(`${raceId} GMT+0900`).getTime();
}

/**
 * Get player's score on active state.
 * @param state
 * @param now
 * @returns
 */
export function getActiveRaceState(
  state: State,
  now: number,
): Immutable<RaceState> | undefined {
  // No active race, no score.
  const activeRace = getActiveRace(now);
  if (!activeRace) {
    return undefined;
  }

  // User hasn't joined race yet, no score.
  const races = state.match2.races[activeRace.id];
  if (!races) {
    return undefined;
  }

  // Last race over, no score.
  const lastRace = races[races.length - 1];
  if (lastRace.claimedRewardAt) {
    return undefined;
  }

  // Return last race state.
  return lastRace;
}

/**
 * Check if player joined active event.
 * @param state
 * @param now
 * @returns
 */
export function hasJoinedActiveRace(state: State, now: number): boolean {
  return !!getActiveRaceState(state, now);
}

/**
 * Get oponents' score on active race.
 * @param state
 * @param now
 * @returns
 */
export function getRaceOpponents(
  state: State,
  raceId: string,
  raceIndex: number,
  now: number,
): { name: string; score: number; avatar: string }[] {
  // No active races, no scores.
  const raceState = state.match2.races[raceId]?.[raceIndex];
  if (!raceState) {
    return [];
  }

  let results: { name: string; score: number; avatar: string }[] = [];
  const { config } = getActiveRace(raceState.createdAt)!;

  if (raceState.bots) {
    switch (config.type) {
      case 'time':
        results = getTimedRaceOpponentsV2(state, raceId, raceIndex);
        break;
      case 'target':
        results = getTargetRaceOpponentsV2(state, raceId, raceIndex);
        break;
      default:
        assertNever(config);
    }

    // Change bot name to be the bot type.
    if (process.env.IS_DEVELOPMENT) {
      const raceRNG = createPrng(`${raceState.createdAt}`);
      const composition = getRandomItemFromArray(botCompositionsV2, raceRNG);
      results.forEach((result, index) => {
        result.name = composition[index];
      });
    }

    return results;
  }

  switch (config.type) {
    case 'time':
      results = getTimedRaceOpponents(state, raceId, raceIndex, now, config);
      break;
    case 'target':
      results = getTargetRaceOpponents(state, raceId, raceIndex, now);
      break;
    default:
      assertNever(config);
  }

  return results;
}

function getBotSeed(botIndex: number, raceId: string, raceIndex: number) {
  return 'bot' + botIndex + raceId + (raceIndex || '');
}

function getTargetRaceOpponents(
  state: State,
  raceId: string,
  raceIndex: number,
  now: number,
): { name: string; score: number; avatar: string }[] {
  // No active races, no scores.
  const raceState = state.match2.races[raceId]?.[raceIndex];
  if (!raceState) {
    return [];
  }

  const { createdAt, modifiedAt, score } = raceState;
  const { target, type, goal } = getActiveRace(createdAt)!
    .config as TargetRaceConfig;

  // Get bot composition.
  const raceRNG = createPrng(raceId + raceIndex);
  const composition = getRandomItemFromArray(botCompositions, raceRNG);

  // Cap time to when the target was reached by the player.
  if (score >= target) {
    now = Math.min(modifiedAt, now);
  }

  // Cap time to when the target was reached by a bot.
  const botsOnTargetStamp = [];
  for (let i = 0; i < botCount; i++) {
    if (composition[i] === 'dud') continue;

    const rng = createPrng(getBotSeed(i, raceId, raceIndex));
    const randomFactor = getRandomFloat(0, 1, rng);
    const time = 300 * Math.floor(target / randomFactor);

    // Get the time when the target was reached.
    const endTime = createdAt + duration({ seconds: time });
    botsOnTargetStamp.push(endTime);

    // Check if bot reached the target based on player's score.
    const playerOffset = getRandomInt(-3, 3, rng);
    const botScore = score + playerOffset;
    if (botScore >= target) {
      botsOnTargetStamp[botsOnTargetStamp.length - 1] = modifiedAt;
    }
  }

  // Get the stamp of the 3rd bot. This means that 3 bots have reached the target.
  if (botsOnTargetStamp.length >= 3) {
    botsOnTargetStamp.sort((a, b) => a - b);
    now = Math.min(botsOnTargetStamp[2], now);
  }

  // Generate bots.
  const timeSinceStartS = (now - createdAt) / duration({ seconds: 1 });
  const opponents = [];
  for (let i = 0; i < botCount; i++) {
    const opponent = getRaceOpponent(
      type,
      goal,
      getBotSeed(i, raceId, raceIndex),
      composition[i],
      timeSinceStartS,
      raceState.score,
      state.settings.locale,
    );

    // Make sure the score is capped to the target.
    if (opponent.score >= target) {
      opponent.score = target;
    }

    opponents.push(opponent);
  }

  // Sort by score.
  opponents.sort((a, b) => b.score - a.score);

  return opponents;
}

function getTargetRaceOpponentsV2(
  state: State,
  raceId: string,
  raceIndex: number,
): { name: string; score: number; avatar: string }[] {
  // No active races, no scores.
  const raceState = state.match2.races[raceId]?.[raceIndex];
  if (!raceState) {
    return [];
  }

  // Generate bots.
  const opponents = [];
  for (let i = 0; i < botCount; i++) {
    opponents.push(
      getRaceOpponentV2(
        state,
        raceId,
        raceIndex,
        i,
        getBotSeed(i, raceId, raceIndex),
      ),
    );
  }

  // Sort by score.
  opponents.sort((a, b) => b.score - a.score);

  return opponents;
}

function getTimedRaceOpponents(
  state: State,
  raceId: string,
  raceIndex: number,
  now: number,
  config: TimedRaceConfig,
): { name: string; score: number; avatar: string }[] {
  // No active races, no scores.
  const raceState = state.match2.races[raceId]?.[raceIndex];
  if (!raceState) {
    return [];
  }

  // Only have different bot compositions if the test is enabled.
  const composition: BotType[] = ['default', 'default', 'default', 'default'];

  // Limit time to duration of the event.
  const { raceDurationHours, goal, type } = config;
  const { createdAt } = raceState;
  let raceFinishedAt =
    createdAt +
    duration({
      hours: raceDurationHours,
    });
  raceFinishedAt = Math.min(raceFinishedAt, getRaceEndDate(raceId));
  now = Math.min(now, raceFinishedAt);

  // Generate bots.
  const timeSinceStartS = (now - createdAt) / duration({ seconds: 1 });
  const opponents = [];
  for (let i = 0; i < botCount; i++) {
    opponents.push(
      getRaceOpponent(
        type,
        goal,
        getBotSeed(i, raceId, raceIndex),
        composition[i],
        timeSinceStartS,
        raceState.score,
        state.settings.locale,
      ),
    );
  }

  // Sort by score.
  opponents.sort((a, b) => b.score - a.score);

  return opponents;
}

function getTimedRaceOpponentsV2(
  state: State,
  raceId: string,
  raceIndex: number,
): { name: string; score: number; avatar: string }[] {
  // No active races, no scores.
  const raceState = state.match2.races[raceId]?.[raceIndex];
  if (!raceState) {
    return [];
  }

  // Generate bots.
  const opponents = [];
  for (let i = 0; i < botCount; i++) {
    opponents.push(
      getRaceOpponentV2(
        state,
        raceId,
        raceIndex,
        i,
        getBotSeed(i, raceId, raceIndex),
      ),
    );
  }

  // Sort by score.
  opponents.sort((a, b) => b.score - a.score);

  return opponents;
}

function getRaceOpponent(
  raceType: RaceType,
  raceGoal: RaceGoal,
  seed: string,
  botType: BotType,
  timeSinceStartS: number,
  playerScore: number,
  locale: string,
) {
  // Randomize score based on bot id.
  const rng = createPrng(seed);

  let score = 0;

  switch (botType) {
    case 'default':
      score = defaultBotScore(
        raceType,
        raceGoal,
        playerScore,
        timeSinceStartS,
        rng,
      );
      break;
    case 'competitive':
      score = competitiveBotScore(
        raceType,
        raceGoal,
        playerScore,
        timeSinceStartS,
        rng,
      );
      break;
    case 'dud':
      score = dudBotScore(raceType, raceGoal, timeSinceStartS);
      break;
    default:
      assertNever(botType);
  }

  // Keep score at 0 for the first minute.
  if (timeSinceStartS < 60) {
    score = 0;
  }

  // Generate bot data. Order is important for consistent random results.
  const name = generateRandomName(locale as Locale, rng);
  const avatar = getRandomItemFromArray<string>(BOT_AVATARS, rng);

  return {
    name,
    score,
    avatar,
  };
}

function getRaceOpponentV2(
  state: State,
  raceId: string,
  raceIndex: number,
  botIndex: number,
  seed: string,
) {
  // Randomize score based on bot id.
  const rng = createPrng(seed);

  // Generate bot data. Order is important for consistent "random" results.
  const locale = state.settings.locale as Locale;
  const name = generateRandomName(locale, rng);
  const avatar = getRandomItemFromArray<string>(BOT_AVATARS, rng);

  const score =
    state.match2.races[raceId]?.[raceIndex]?.bots?.[botIndex]?.score ?? 0;

  return {
    name,
    score,
    avatar,
  };
}

function defaultBotScore(
  raceType: RaceType,
  raceGoal: RaceGoal,
  playerScore: number,
  timeSinceStartS: number,
  rng: () => number,
) {
  const randomFactor = getRandomFloat(0, 1, rng);
  const timeFactor = timeSinceStartS / 300;
  const factors = scoreFactors[raceType];
  const scoreFactor = ((factors as any)[raceGoal] as number) ?? 1;
  const score = Math.ceil(randomFactor * timeFactor * scoreFactor);

  // Cap score to player's score for timed races.
  if (raceType === 'time') {
    const capRandomFactor = getRandomInt(1, 3, rng);
    const capFactor = capRandomFactor;
    const playerScoreCap = 2;
    return Math.min(score, Math.max(playerScore * playerScoreCap, capFactor));
  }

  return score;
}

function competitiveBotScore(
  raceType: RaceType,
  raceGoal: RaceGoal,
  playerScore: number,
  timeSinceStartS: number,
  rng: () => number,
) {
  const defaultScore = defaultBotScore(
    raceType,
    raceGoal,
    playerScore,
    timeSinceStartS,
    rng,
  );
  const randomFactor = getRandomInt(-3, 3, rng);
  const factors = scoreFactors[raceType];
  const scoreFactor = ((factors as any)[raceGoal] as number) ?? 1;
  return Math.max(
    scoreFactor * (playerScore / scoreFactor + randomFactor),
    defaultScore,
  );
}

function dudBotScore(
  raceType: RaceType,
  raceGoal: RaceGoal,
  timeSinceStartS: number,
) {
  const timeSinceStartM = timeSinceStartS / 60;
  // Give 10 sugar every 15 minutes.
  if (raceType === 'time' && raceGoal === 'lollipop') {
    return Math.floor(timeSinceStartM / 15) * 10;
  }

  // Give 1 level every 30 minutes.
  return Math.floor(timeSinceStartM / 30);
}

/**
 * Get unclaimed rewards.
 * @param state
 * @param now
 * @returns
 */
export function getUnclaimedRaces(
  state: State,
  now: number,
): [string, number][] {
  const unclaimed: [string, number][] = [];
  Object.keys(state.match2.races).forEach((raceId) =>
    state.match2.races[raceId].forEach((_, raceIndex) => {
      if (canClaimRaceRewards(state, raceId, raceIndex, now)) {
        unclaimed.push([raceId, raceIndex]);
      }
    }),
  );

  return unclaimed;
}

/**
 * Check if player can claim rewards.
 * @param state
 * @param raceId
 * @param raceIndex
 * @param now
 */
export function canClaimRaceRewards(
  state: State,
  raceId: string,
  raceIndex: number,
  now: number,
): boolean {
  const race = state.match2.races[raceId]?.[raceIndex];

  // User has not participated on the race.
  if (!race) return false;

  // Already claimed.
  if (race.claimedRewardAt) return false;

  const activeRace = getActiveRace(race.createdAt);

  // No active race, something is possibly wrong. But handle it gracefully.
  if (!activeRace) {
    return false;
  }

  // Event has not ended.
  const { config } = getActiveRace(race.createdAt)!;
  switch (config.type) {
    case 'time':
      return canClaimTimedRaceRewards(state, raceId, raceIndex, now, config);
    case 'target':
      return canClaimTargetRaceRewards(state, raceId, raceIndex, now, config);
    default:
      assertNever(config);
  }
}

function canClaimTimedRaceRewards(
  state: State,
  raceId: string,
  raceIndex: number,
  now: number,
  config: TimedRaceConfig,
) {
  const race = state.match2.races[raceId]![raceIndex]!;
  const { raceDurationHours } = config;
  let raceFinishedAt =
    race.createdAt +
    duration({
      hours: raceDurationHours,
    });
  raceFinishedAt = Math.min(raceFinishedAt, getRaceEndDate(raceId));
  if (raceFinishedAt > now) {
    return false;
  }

  return true;
}

function canClaimTargetRaceRewards(
  state: State,
  raceId: string,
  raceIndex: number,
  now: number,
  config: TargetRaceConfig,
) {
  const raceState = state.match2.races[raceId]?.[raceIndex];
  // No race state, no rewards.
  if (!raceState) {
    return false;
  }

  // No one has reached the target score.
  const opponentsScore = getRaceOpponents(state, raceId, raceIndex, now).map(
    (x) => x.score,
  );
  if (
    raceState.score < config.target &&
    opponentsScore.filter((x) => x >= config.target).length < 3
  ) {
    return false;
  }

  return true;
}

/**
 * Retrieve the rewards config for the feature
 * @returns
 */
export function getRaceRewardsConfig() {
  return rewardsConfig;
}

/**
 * Get race rewards.
 * @param state
 * @param raceId
 * @param raceIndex
 * @param now
 */
export function getRaceRewards(
  state: State,
  raceId: string,
  raceIndex: number,
  now: number,
) {
  // Figure out rewards.
  const racePosition = getRacePosition(state, raceId, raceIndex, now);
  const raceType = getActiveRace(getRaceStartTime(raceId))!.config.type;
  const config = getRaceRewardsConfig();
  const rewards = config[raceType][racePosition - 1];

  // No rewards.
  if (!rewards) return [];

  return rewards;
}

/**
 * Get position of the user on givn race.
 * @param state
 * @param raceId
 * @param raceIndex
 * @param now
 * @returns
 */
export function getRacePosition(
  state: State,
  raceId: string,
  raceIndex: number,
  now: number,
): number {
  const race = state.match2.races[raceId]?.[raceIndex];

  // User has not participated on race. No rewards.
  if (!race) return Infinity;

  // Get all scores.
  const scores: { name: string; score: number }[] = [
    ...getRaceOpponents(state, raceId, raceIndex, now),
    { name: state.id, score: race.score },
  ];

  // Sort scores.
  scores.sort((a, b) => b.score - a.score);

  return scores.findIndex(({ name }) => name === state.id) + 1;
}

/**
 * Get sorted race scores.
 * @param state
 * @param raceId
 * @param raceIndex
 * @param now
 * @returns
 */
export function getRaceScores(
  state: State,
  raceId: string,
  raceIndex: number,
  now: number,
): number[] {
  const race = state.match2.races[raceId]?.[raceIndex];

  // User has not participated on race. Empty scores.
  if (!race) return [];

  // Get all scores.
  const scores = [
    ...getRaceOpponents(state, raceId, raceIndex, now).map((x) => x.score),
    race.score,
  ];
  // Sort scores.
  scores.sort((a, b) => b - a);

  return scores;
}
